import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// Deep-link hash scrolling. BrowserRouter does no hash handling, and on a cold
// load of /#some-id the browser's native anchor jump fires during initial HTML
// parse — when #root is still empty and React has not mounted the target yet —
// so it silently no-ops and the page stays at scrollY=0 (email CTA to
// /#acces-porte landed at the top instead of the door-access video).
//
// We re-attempt the scroll after mount. The target may not exist on the first
// tick (lazy route + <Suspense>), so we poll on a bounded rAF loop until it
// appears, then scroll once. Bounded so it can never spin forever, cancelled on
// hash change / unmount so a stale loop can't fight a later navigation.
// ponytail: rAF loop, not a MutationObserver — same coverage, no observer to tear down.
const MAX_FRAMES = 50 // ~800ms at 60fps — covers the lazy-mount delay

export default function ScrollToHash() {
  const { hash, key } = useLocation()

  useEffect(() => {
    if (!hash) return
    // hash is user-controlled — a malformed %-sequence (/#%8) makes
    // decodeURIComponent throw URIError, which would crash this root-level
    // component. Fall back to the raw id rather than take the app down.
    let id: string
    try {
      id = decodeURIComponent(hash.slice(1))
    } catch {
      id = hash.slice(1)
    }
    let frame = 0
    let raf = 0

    const attempt = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView()
        return
      }
      if (frame++ < MAX_FRAMES) raf = requestAnimationFrame(attempt)
    }
    attempt()

    return () => cancelAnimationFrame(raf)
    // `key` changes on every navigation, so a repeat click on the same hash
    // re-triggers the scroll even when `hash` itself is unchanged.
  }, [hash, key])

  return null
}
